/**
 * Which identity a digest belongs to. Each is keyed the way PostHog stores its
 * properties: a person by its uuid alone, since its properties are the same in
 * every workspace, and a group by its workspace id alone, since every user of
 * the group shares one property set. A group has no key while the workspace id
 * is undefined, so nothing is read or written under an unresolved workspace.
 */
export type tIdentityHashKeyOptions = {
  kind: 'person' | 'group';
  /** User uuid: keys a person, ignored for a group. */
  uuid?: string;
  /** Workspace id: keys a group, ignored for a person. */
  workspaceId?: string;
};

/** Hash of a property set plus one hash per key; it holds no raw values. */
export type tIdentityDigest = {
  hash: string;
  propertyHashes: Record<string, string>;
};

/** A digest and the time, in epoch milliseconds, its properties were sent. */
export type tIdentityHashEntry = tIdentityDigest & { sentAt: number };

const STORAGE_KEY_PREFIX = 'analyticsIdentityHash';

// localStorage can fail a write and still answer reads, so every write lands
// here first and every read checks here before storage.
const inMemoryEntries = new Map<string, tIdentityHashEntry>();

/** 53-bit string hash (cyrb53): fast, synchronous, not reversible to the value. */
function hashString(value: string): string {
  let first = 0xdeadbeef;
  let second = 0x41c6ce57;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    first = Math.imul(first ^ code, 2654435761);
    second = Math.imul(second ^ code, 1597334677);
  }
  first =
    Math.imul(first ^ (first >>> 16), 2246822507) ^
    Math.imul(second ^ (second >>> 13), 3266489909);
  second =
    Math.imul(second ^ (second >>> 16), 2246822507) ^
    Math.imul(first ^ (first >>> 13), 3266489909);
  return (4294967296 * (2097151 & second) + (first >>> 0)).toString(36);
}

/**
 * Builds the digest of a property set. `$set_once` and keys whose value is
 * undefined are left out because neither changes what the SDK would set; null
 * and the empty string are kept because they do. Key order does not matter.
 *
 * @param properties - The properties sent to the SDK.
 * @returns The overall hash and the hash of each key.
 */
export function buildIdentityDigest(
  properties: Record<string, unknown>,
): tIdentityDigest {
  const propertyHashes: Record<string, string> = {};
  Object.keys(properties)
    .filter((key) => key !== '$set_once' && properties[key] !== undefined)
    .sort()
    .forEach((key) => {
      propertyHashes[key] = hashString(JSON.stringify(properties[key]));
    });
  const hash = hashString(
    Object.entries(propertyHashes)
      .map(([key, propertyHash]) => `${key}=${propertyHash}`)
      .join('&'),
  );
  return { hash, propertyHashes };
}

/**
 * Builds the localStorage key of an identity digest.
 *
 * @param keyOptions - Kind of the identity and the id it is keyed by.
 * @returns The key, or undefined when the id the kind is keyed by is missing.
 */
export function buildIdentityHashStorageKey(
  keyOptions: tIdentityHashKeyOptions,
): string | undefined {
  if (keyOptions.kind === 'person') {
    if (!keyOptions.uuid) return undefined;
    return `${STORAGE_KEY_PREFIX}:person:${keyOptions.uuid}`;
  }
  if (keyOptions.workspaceId === undefined) return undefined;
  return `${STORAGE_KEY_PREFIX}:group:${keyOptions.workspaceId}`;
}

function isIdentityHashEntry(value: unknown): value is tIdentityHashEntry {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.hash === 'string' &&
    typeof candidate.sentAt === 'number' &&
    typeof candidate.propertyHashes === 'object' &&
    candidate.propertyHashes !== null
  );
}

/**
 * Reads the last-sent entry of an identity, from memory first and then from
 * localStorage. A corrupt or unreadable stored value reads as undefined.
 *
 * @param keyOptions - Kind, user uuid and workspace id of the identity.
 * @returns The entry, or undefined when none is known.
 */
export function readIdentityHashEntry(
  keyOptions: tIdentityHashKeyOptions,
): tIdentityHashEntry | undefined {
  const storageKey = buildIdentityHashStorageKey(keyOptions);
  if (storageKey === undefined) return undefined;
  const inMemoryEntry = inMemoryEntries.get(storageKey);
  if (inMemoryEntry) return inMemoryEntry;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!isIdentityHashEntry(parsed)) return undefined;
    inMemoryEntries.set(storageKey, parsed);
    return parsed;
  } catch {
    return undefined;
  }
}

/**
 * Stores the last-sent entry of an identity in memory and in localStorage. A
 * failed storage write is swallowed: the in-memory copy still prevents a
 * resend until the page reloads.
 *
 * @param keyOptions - Kind, user uuid and workspace id of the identity.
 * @param entry - The digest and send time to store.
 */
export function writeIdentityHashEntry(
  keyOptions: tIdentityHashKeyOptions,
  entry: tIdentityHashEntry,
): void {
  const storageKey = buildIdentityHashStorageKey(keyOptions);
  if (storageKey === undefined) return;
  inMemoryEntries.set(storageKey, entry);
  try {
    localStorage.setItem(storageKey, JSON.stringify(entry));
  } catch {
    // Quota or blocked storage: the in-memory copy above covers this page load.
  }
}

/** Clears the in-memory copies, as a page reload would; localStorage is kept. */
export function resetIdentityHashStoreForTests(): void {
  inMemoryEntries.clear();
}
